import React, { useEffect, useMemo, useState } from "react";
import API from "../../api/api";
import adminApi from "../../api/adminApi";
import "./Admin.css";

const emptyQuestion = () => ({
  questionText: "",
  imageUrl: "",
  imagePublicId: "",
  imageAlt: "",
  codeSnippet: "",
  codeLanguage: "",
  options: ["", "", "", ""],
  correctAnswer: "",
});

const CODE_LANGUAGES = [
  { value: "", label: "No code" },
  { value: "python", label: "Python" },
  { value: "javascript", label: "JavaScript" },
  { value: "java", label: "Java" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "html", label: "HTML" },
  { value: "css", label: "CSS" },
  { value: "sql", label: "SQL" },
  { value: "json", label: "JSON" },
  { value: "text", label: "Plain Text" },
];

const initialForm = {
  title: "",
  description: "",
  level: "Easy",
  technologies: "",
  timeLimit: 30,
  passMarks: 1,
  startTimeIst: "",
  endTimeIst: "",
  questions: [emptyQuestion()],
};

const toUtcIsoFromIstInput = (value) => {
  if (!value) return null;
  return new Date(`${value}:00+05:30`).toISOString();
};

const toIstInputFromUtc = (utcDate) => {
  const d = new Date(utcDate);
  const istMs = d.getTime() + 5.5 * 60 * 60 * 1000;
  const istDate = new Date(istMs);
  return istDate.toISOString().slice(0, 16);
};

const toIstText = (utcDate) =>
  new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(utcDate));

const normalizeQuestions = (questions) =>
  questions.map((q) => ({
    questionText: String(q.questionText || "").trim(),
    imageUrl: String(q.imageUrl || "").trim(),
    imagePublicId: String(q.imagePublicId || "").trim(),
    imageAlt: String(q.imageAlt || "").trim(),
    codeSnippet: String(q.codeSnippet || ""),
    codeLanguage: String(q.codeLanguage || "").trim(),
    options: Array.isArray(q.options)
      ? q.options.map((o) => String(o || "").trim()).slice(0, 4)
      : ["", "", "", ""],
    correctAnswer: String(q.correctAnswer || "").trim(),
  }));

const AdminPrivateExams = () => {
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState("");
  const [privateExams, setPrivateExams] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignExamId, setAssignExamId] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [jsonInput, setJsonInput] = useState("");
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);
  const [originalImagePublicIds, setOriginalImagePublicIds] = useState([]);

  const selectedExam = useMemo(
    () => privateExams.find((exam) => exam._id === assignExamId) || null,
    [privateExams, assignExamId]
  );

  const filteredUsers = useMemo(() => {
    if (!searchUser.trim()) return users;
    const s = searchUser.toLowerCase();
    return users.filter(
      (u) => u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s)
    );
  }, [users, searchUser]);

  const loadExams = async () => {
    try {
      setLoading(true);
      const res = await API.get("/admin/private-exams");
      setPrivateExams(res.data?.exams || []);
    } catch (err) {
      setError(err?.message || "Failed to load private exams");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await API.get("/admin/users", { params: { page: 1, limit: 200 } });
      setUsers((res.data?.users || []).filter((u) => u.role === "USER" && u.isActive && !u.isSuspended));
    } catch (err) {
      setError(err?.message || "Failed to load users");
    }
  };

  useEffect(() => {
    loadExams();
    loadUsers();
  }, []);

  const addQuestion = () => {
    setForm((prev) => ({ ...prev, questions: [...prev.questions, emptyQuestion()] }));
  };

  const updateQuestion = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }));
  };

  const updateOption = (qIndex, oIndex, value) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIndex) return q;
        const options = [...q.options];
        options[oIndex] = value;
        const correctAnswer = q.correctAnswer === q.options[oIndex] ? value : q.correctAnswer;
        return { ...q, options, correctAnswer };
      }),
    }));
  };

  const removeQuestion = async (index) => {
    const publicId = form.questions[index]?.imagePublicId;
    const shouldDeleteNow = publicId && (!editingId || !originalImagePublicIds.includes(publicId));
    if (shouldDeleteNow) {
      try {
        await adminApi.deleteQuestionImage(publicId);
      } catch (err) {
        console.error("Failed to delete removed private question image:", err);
      }
    }

    setForm((prev) => {
      if (prev.questions.length <= 1) return prev;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== index) };
    });
  };

  const uploadQuestionImage = async (qIndex, file) => {
    if (!file) return;
    setError("");
    setUploadingImageIndex(qIndex);

    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await adminApi.uploadQuestionImage(formData);
      updateQuestion(qIndex, {
        imageUrl: res.data.imageUrl,
        imagePublicId: res.data.imagePublicId,
        imageAlt: form.questions[qIndex]?.questionText || file.name,
      });
      setSuccess("Question image uploaded.");
    } catch (err) {
      setError(err?.message || "Failed to upload question image.");
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const removeQuestionImage = async (qIndex) => {
    const publicId = form.questions[qIndex]?.imagePublicId;
    try {
      if (publicId && (!editingId || !originalImagePublicIds.includes(publicId))) {
        await adminApi.deleteQuestionImage(publicId);
      }
      updateQuestion(qIndex, { imageUrl: "", imagePublicId: "", imageAlt: "" });
      setSuccess("Question image removed.");
    } catch (err) {
      setError(err?.message || "Failed to remove question image.");
    }
  };

  const cleanupUnsavedImages = async (questions = form.questions) => {
    const originalSet = new Set(originalImagePublicIds);
    const imageIds = questions
      .map((q) => q.imagePublicId)
      .filter((publicId) => publicId && !originalSet.has(publicId));

    await Promise.all(
      imageIds.map((publicId) =>
        adminApi.deleteQuestionImage(publicId).catch((err) => {
          console.error("Failed to cleanup private exam image:", err);
        })
      )
    );
  };

  const parseJsonQuestions = async () => {
    try {
      setError("");
      if (!editingId) await cleanupUnsavedImages();
      const parsed = JSON.parse(jsonInput);
      const payload = parsed?.questions ? parsed : parsed?.quiz || parsed;
      const questions = normalizeQuestions(payload.questions || []);
      if (!questions.length) throw new Error("JSON must include questions array");
      setForm((prev) => ({
        ...prev,
        title: payload.title || prev.title,
        description: payload.description || prev.description,
        level: payload.level || prev.level,
        timeLimit: payload.timeLimit || prev.timeLimit,
        passMarks: payload.passMarks || prev.passMarks,
        technologies: Array.isArray(payload.technologies)
          ? payload.technologies.join(", ")
          : prev.technologies,
        questions,
      }));
      setSuccess(`Imported ${questions.length} questions from JSON`);
    } catch (err) {
      setError(err?.message || "Invalid JSON");
    }
  };

  const validateForm = () => {
    if (!form.title.trim()) throw new Error("Title is required.");
    if (!form.startTimeIst || !form.endTimeIst) throw new Error("Start and end times are required.");
    const start = new Date(`${form.startTimeIst}:00+05:30`);
    const end = new Date(`${form.endTimeIst}:00+05:30`);
    if (end <= start) throw new Error("End time must be after start time.");
    form.questions.forEach((q, idx) => {
      if (!q.questionText.trim()) throw new Error(`Question ${idx + 1} text required.`);
      if (q.options.some((o) => !String(o).trim())) throw new Error(`Question ${idx + 1} requires 4 options.`);
      if (!q.correctAnswer || !q.options.includes(q.correctAnswer)) {
        throw new Error(`Question ${idx + 1} correct answer must match an option.`);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      validateForm();
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        level: form.level,
        technologies: form.technologies
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        timeLimit: Number(form.timeLimit),
        passMarks: Number(form.passMarks),
        startTime: toUtcIsoFromIstInput(form.startTimeIst),
        endTime: toUtcIsoFromIstInput(form.endTimeIst),
        questions: normalizeQuestions(form.questions),
      };

      if (editingId) {
        await API.put(`/admin/private-exams/${editingId}`, payload);
        setSuccess("Private exam updated.");
      } else {
        await API.post("/admin/private-exams/create", payload);
        setSuccess("Private exam created.");
      }

      setForm(initialForm);
      setEditingId("");
      setOriginalImagePublicIds([]);
      setJsonInput("");
      loadExams();
    } catch (err) {
      setError(err?.message || "Failed to save private exam");
    }
  };

  const startEdit = (exam) => {
    setEditingId(exam._id);
    setOriginalImagePublicIds(
      (exam.questions || []).map((q) => q.imagePublicId).filter(Boolean)
    );
    setForm({
      title: exam.title || "",
      description: exam.description || "",
      level: exam.level || "Easy",
      technologies: Array.isArray(exam.technologies) ? exam.technologies.join(", ") : "",
      timeLimit: exam.timeLimit || 30,
      passMarks: exam.passMarks || 1,
      startTimeIst: toIstInputFromUtc(exam.startTime),
      endTimeIst: toIstInputFromUtc(exam.endTime),
      questions: normalizeQuestions(exam.questions || [emptyQuestion()]),
    });
  };

  const deleteExam = async (id) => {
    try {
      setError("");
      await API.delete(`/admin/private-exams/${id}`);
      setSuccess("Private exam deleted.");
      loadExams();
    } catch (err) {
      setError(err?.message || "Delete failed");
    }
  };

  const assignExam = async () => {
    try {
      setError("");
      setSuccess("");
      if (!assignExamId) throw new Error("Select exam first.");
      if (!selectedUserIds.length) throw new Error("Select at least one user.");
      await API.post(`/admin/private-exams/${assignExamId}/assign`, { userIds: selectedUserIds });
      setSuccess("Users assigned and notified successfully.");
      setSelectedUserIds([]);
      loadExams();
    } catch (err) {
      setError(err?.message || "Assignment failed");
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  return (
    <section className="admin-page">
      <div className="admin-card">
        <h3>{editingId ? "Edit Private Exam" : "Create Private Exam"}</h3>
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-form-grid">
            <input
              type="text"
              placeholder="Exam Title"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              required
            />
            <select value={form.level} onChange={(e) => setForm((p) => ({ ...p, level: e.target.value }))}>
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
            <input
              type="number"
              min="1"
              placeholder="Time Limit (min)"
              value={form.timeLimit}
              onChange={(e) => setForm((p) => ({ ...p, timeLimit: e.target.value }))}
              required
            />
            <input
              type="number"
              min="1"
              placeholder="Pass Marks"
              value={form.passMarks}
              onChange={(e) => setForm((p) => ({ ...p, passMarks: e.target.value }))}
              required
            />
            <input
              type="datetime-local"
              value={form.startTimeIst}
              onChange={(e) => setForm((p) => ({ ...p, startTimeIst: e.target.value }))}
              required
            />
            <input
              type="datetime-local"
              value={form.endTimeIst}
              onChange={(e) => setForm((p) => ({ ...p, endTimeIst: e.target.value }))}
              required
            />
            <textarea
              rows={2}
              placeholder="Description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
            <input
              type="text"
              placeholder="Technologies (comma separated)"
              value={form.technologies}
              onChange={(e) => setForm((p) => ({ ...p, technologies: e.target.value }))}
            />
          </div>

          <div className="admin-json-block">
            <label htmlFor="private-json">Question JSON (optional import)</label>
            <textarea
              id="private-json"
              rows={6}
              className="admin-json-input"
              placeholder="Paste JSON with questions[]"
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
            />
            <div className="actions-row">
              <button type="button" className="secondary" onClick={parseJsonQuestions}>
                Import Questions JSON
              </button>
            </div>
          </div>

          <div className="admin-question-builder">
            <div className="admin-question-head">
              <h4>Question Builder</h4>
              <button type="button" className="secondary" onClick={addQuestion}>
                + Add Question
              </button>
            </div>
            {form.questions.map((q, idx) => (
              <div key={`private-q-${idx}`} className="admin-question-card">
                <div className="admin-question-card-head">
                  <strong>Question {idx + 1}</strong>
                  <button type="button" className="danger" onClick={() => removeQuestion(idx)}>
                    Delete
                  </button>
                </div>
                <textarea
                  rows={2}
                  placeholder="Question text"
                  value={q.questionText}
                  onChange={(e) => updateQuestion(idx, { questionText: e.target.value })}
                />
                <div className="question-image-uploader">
                  {q.imageUrl ? (
                    <div className="question-image-preview">
                      <img src={q.imageUrl} alt={q.imageAlt || `Question ${idx + 1}`} />
                      <button type="button" className="danger" onClick={() => removeQuestionImage(idx)}>
                        Remove Image
                      </button>
                    </div>
                  ) : (
                    <label className="question-image-drop">
                      <span>
                        {uploadingImageIndex === idx ? "Uploading image..." : "Upload image for this question"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingImageIndex === idx}
                        onChange={(e) => uploadQuestionImage(idx, e.target.files?.[0])}
                      />
                    </label>
                  )}
                  {q.imageUrl ? (
                    <input
                      type="text"
                      placeholder="Image alt text"
                      value={q.imageAlt}
                      onChange={(e) => updateQuestion(idx, { imageAlt: e.target.value })}
                    />
                  ) : null}
                </div>
                <div className="question-code-editor">
                  <select
                    value={q.codeLanguage}
                    onChange={(e) => updateQuestion(idx, { codeLanguage: e.target.value })}
                  >
                    {CODE_LANGUAGES.map((item) => (
                      <option key={item.value || "none"} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <textarea
                    rows={6}
                    placeholder="Optional code/program for this question"
                    value={q.codeSnippet}
                    onChange={(e) => updateQuestion(idx, { codeSnippet: e.target.value })}
                  />
                </div>
                <div className="admin-options-grid">
                  {q.options.map((option, optionIndex) => (
                    <div key={`private-q-${idx}-o-${optionIndex}`} className="admin-option-row">
                      <input
                        type="radio"
                        name={`private-correct-${idx}`}
                        checked={q.correctAnswer === option && option !== ""}
                        onChange={() => updateQuestion(idx, { correctAnswer: option })}
                      />
                      <input
                        type="text"
                        placeholder={`Option ${optionIndex + 1}`}
                        value={option}
                        onChange={(e) => updateOption(idx, optionIndex, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="actions-row">
            <button type="submit">{editingId ? "Update Private Exam" : "Create Private Exam"}</button>
            {editingId ? (
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  cleanupUnsavedImages().finally(() => {
                    setEditingId("");
                    setOriginalImagePublicIds([]);
                    setForm(initialForm);
                  });
                }}
              >
                Cancel Edit
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h3>Assign Private Exam</h3>
        <div className="admin-form-grid">
          <select value={assignExamId} onChange={(e) => setAssignExamId(e.target.value)}>
            <option value="">Select exam</option>
            {privateExams.map((exam) => (
              <option key={exam._id} value={exam._id}>
                {exam.title} ({exam.computedState})
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search user by name/email"
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
          />
        </div>
        <div className="admin-user-pick-list">
          {filteredUsers.map((user) => (
            <label key={user._id} className="admin-user-pick-item">
              <input
                type="checkbox"
                checked={selectedUserIds.includes(user._id)}
                onChange={() => toggleUserSelection(user._id)}
              />
              <span>{user.name}</span>
              <small>{user.email}</small>
            </label>
          ))}
        </div>
        <div className="actions-row">
          <button type="button" onClick={assignExam}>Assign Selected Users</button>
          {selectedExam ? (
            <span className="admin-json-hint">
              {selectedUserIds.length} selected for <strong>{selectedExam.title}</strong>
            </span>
          ) : null}
        </div>
      </div>

      <div className="admin-card admin-table-wrap">
        <h3>Private Exams</h3>
        {loading ? <div className="admin-state">Loading private exams...</div> : null}
        {error ? <div className="admin-state admin-error">{error}</div> : null}
        {success ? <div className="admin-state">{success}</div> : null}

        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Window (IST)</th>
              <th>State</th>
              <th>Assignments</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {privateExams.map((exam) => (
              <tr key={exam._id}>
                <td>
                  <div>{exam.title}</div>
                  <small>{exam.quizId}</small>
                </td>
                <td>
                  <div>{toIstText(exam.startTime)}</div>
                  <div>to {toIstText(exam.endTime)}</div>
                </td>
                <td>{exam.computedState}</td>
                <td>
                  <div>Assigned: {exam.assignedCount || 0}</div>
                  <div>Started: {exam.startedCount || 0}</div>
                  <div>Submitted: {exam.submittedCount || 0}</div>
                  <div>Missed: {exam.missedCount || 0}</div>
                </td>
                <td>
                  <div className="actions-row">
                    <button type="button" className="secondary" onClick={() => startEdit(exam)}>Edit</button>
                    <button type="button" className="danger" onClick={() => deleteExam(exam._id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default AdminPrivateExams;
